<template>
  <b-modal
    ref="modal"
    title="Import to Video"
    size="lg"
    scrollable
    @hide="closeHandler"
    @show="onShowHandler"
  >
    <p class="c-import-to-video__intro">
      {{ fileCount }} will be added as new scenes. Choose where.
    </p>

    <button
      type="button"
      class="c-import-to-video__new"
      :class="{ 'c-import-to-video__new--active': isNewProjectTarget }"
      @click="selectNewProject"
    >
      <span class="c-import-to-video__new-icon">
        <LucideIcon
          name="plus"
          :size="16"
        />
      </span>
      New video project
    </button>

    <InputSearch
      class="my-3"
      @search="searchProjects"
    />

    <div
      v-if="isLoading && !projects.length"
      class="text-center py-4"
    >
      <b-spinner />
    </div>
    <p
      v-else-if="!sortedProjects.length"
      class="text-center text-muted py-4 mb-0"
    >
      {{ emptyMessage }}
    </p>
    <ViewList
      v-else
      :items="sortedProjects"
      :selected-id="selectedProjectId"
      @select="selectProject"
    />

    <template #modal-footer>
      <span
        v-if="target"
        class="mr-auto c-import-to-video__summary"
      >
        {{ summary }}
      </span>
      <ButtonOutline @click="close()">
        Cancel
      </ButtonOutline>
      <ButtonPrimary
        :disabled="!target || !selectedItems.length"
        @click="runImport"
      >
        Import
      </ButtonPrimary>
    </template>
  </b-modal>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import mixinModal from './mixin'
import ProjectAPI from '@frontend/services/api/project'
import ButtonOutline from '@frontend/components/common/atoms/ButtonOutline.vue'
import ButtonPrimary from '@frontend/components/common/atoms/ButtonPrimary.vue'
import InputSearch from '@frontend/components/common/atoms/InputSearch.vue'
import LucideIcon from '@frontend/components/common/atoms/LucideIcon.vue'
import ViewList from '@frontend/components/common/ViewList.vue'
import { getFileDisplayName, resolveLibraryItemUrl } from '../helpers'
import { setPendingVideoImport } from '@frontend/utils/video-import-handoff'

const { mapGetters: mapFileBrowserGetters } = createNamespacedHelpers('fileBrowser')

const TARGET_NEW = 'new'
const TARGET_PROJECT = 'project'

const byName = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })

export default {
  name: 'ImportToVideoModal',

  components: {
    ButtonOutline,
    ButtonPrimary,
    InputSearch,
    LucideIcon,
    ViewList,
  },

  mixins: [mixinModal],

  data: () => ({
    target: null,
    search: null,
    isLoading: false,
    loadFailed: false,
    projects: [],
    pendingRequest: null,
  }),

  computed: {
    ...mapFileBrowserGetters(['selectedItems']),

    isNewProjectTarget() {
      return this.target?.type === TARGET_NEW
    },

    selectedProjectId() {
      return this.target?.type === TARGET_PROJECT ? this.target.project.id : null
    },

    sortedProjects() {
      return [...this.projects]
        .sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at)) || byName.compare(a.name, b.name))
    },

    fileCount() {
      return `${this.selectedItems.length} file${this.selectedItems.length === 1 ? '' : 's'}`
    },

    emptyMessage() {
      if (this.loadFailed) {
        return 'The projects could not be loaded. Close this window and try again.'
      }

      return this.search ? 'No projects match your search.' : 'No video projects yet.'
    },

    summary() {
      return this.isNewProjectTarget
        ? `Add ${this.fileCount} to a new project`
        : `Add ${this.fileCount} to "${this.target.project.name}"`
    },
  },

  methods: {
    selectNewProject() {
      this.target = { type: TARGET_NEW }
    },

    selectProject(project) {
      this.target = { type: TARGET_PROJECT, project }
    },

    onShowHandler() {
      this.target = null
      this.search = null
      this.fetchProjects()
    },

    // `include_foldered`: no folder navigation here, so without it a filed-away project is
    // unreachable. Each search aborts the one before it, or a slow early reply can land last.
    async fetchProjects() {
      this.pendingRequest?.abort()

      const request = new AbortController()
      this.pendingRequest = request
      this.isLoading = true

      try {
        const { data } = await ProjectAPI.getProjects(
          { search: this.search, include_foldered: 1 },
          { signal: request.signal }
        )

        this.projects = data.projects || []
        this.loadFailed = false
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error('[ImportToVideoModal]', error)
          this.projects = []
          this.loadFailed = true
        }
      } finally {
        // An aborted request must not stop the spinner the newer one just started.
        if (this.pendingRequest === request) {
          this.isLoading = false
        }
      }
    },

    searchProjects({ search }) {
      this.search = search
      this.fetchProjects()
    },

    runImport() {
      // A background list refresh clears the File Browser selection, and this window stays open.
      if (!this.selectedItems.length) {
        toastr.error('The files are no longer selected. Close this window and select them again.')

        return
      }

      if (!setPendingVideoImport(this.importPayload())) {
        toastr.error('The files could not be handed over to the video editor.')

        return
      }

      window.location.href = this.isNewProjectTarget
        ? '/video'
        : `/video/project/${this.target.project.id}`
    },

    // Same shape VideoMediaLibraryModal hands to VideoTable.selectFiles().
    importPayload() {
      return this.selectedItems.map((item) => {
        const url = resolveLibraryItemUrl(item)

        return {
          id: item.id,
          name: getFileDisplayName(item),
          type: item.type,
          url,
          url_shorten: url,
          duration: 0,
          ...(item.path_to_uncompressed_file ? { path_to_uncompressed_file: item.path_to_uncompressed_file } : {}),
        }
      })
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/../sass/frontend/variables";

.c-import-to-video__intro {
  margin-bottom: 12px;
  font-size: 14px;
  color: $greyscale300;
}

.c-import-to-video__new {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid $greyscale100;
  border-radius: 6px;
  background-color: #fff;
  font-size: 14px;
  font-weight: 600;
  color: $greyscale400;

  &:hover {
    background-color: $greyscale50;
  }

  &--active {
    border-color: $primary200;
    box-shadow: 0 0 0 3px rgba($primary200, 0.15);
  }
}

.c-import-to-video__new-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 28px;
  border-radius: 4px;
  background-color: $greyscale50;
}

.c-import-to-video__summary {
  font-size: 14px;
  color: $greyscale300;
}
</style>
